import { Icons } from "assets";
import React, { useEffect, useState } from "react";
import CustomButton from "shared/components/common/customButton";
import styles from "../style.module.scss";
import {
  ATV_MAKES,
  Boat_MAKES,
  Snow_MAKES,
  UTV_MAKES,
} from "shared/utils/enum";
import * as Yup from "yup";
import { useFormik } from "formik";
import {
  CreateProductBrandService,
  GetProductCategoriesService,
  UpdateProductBrandService,
} from "shared/services/productBrands";
import { toastMessage } from "shared/components/common/toast";
import CustomSelect from "shared/components/common/customSelect";
import classNames from "classnames";
import CustomInput from "shared/components/common/customInput";
import { handleErrors } from "shared/utils/helper";

interface CategoryData {
  id: number;
  title: string;
}

interface EditBrandsProps {
  selectedBrand: any;
  setEditMode: (value: boolean) => void;
}

const EditBrands = ({ selectedBrand, setEditMode }: EditBrandsProps) => {
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<CategoryData[]>([]);

  const validationSchema = Yup.object({
    type: Yup.string().required("Select Product Category"),
    title: Yup.mixed().required("Title is required"),
  });

  const formik = useFormik({
    initialValues: {
      type: "",
      categoryId: "",
      title: selectedBrand?.title ?? "",
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        const formData = new FormData();
        formData.append("type", values.type);
        formData.append("title", values.title);
        await UpdateProductBrandService(formData, selectedBrand?.id);
        toastMessage("success", "Product brand updated successfully!");
        setEditMode(false);
      } catch (error) {
        handleErrors(error);
        toastMessage("error", "Failed to create product brand.");
      } finally {
        setLoading(false);
      }
    },
  });

  const handleCancel = () => {
    setEditMode(false);
  };

  const handleGetAllCategories = async () => {
    try {
      setLoading(true);
      const response = await GetProductCategoriesService();
      setCategories(
        response.data.data.map((item: any) => ({
          id: item.id,
          value: item.title,
          title: item.title,
        }))
      );
    } catch (error) {
      console.error("Error fetching categories:", error);
      toastMessage("error", "Failed to fetch categories.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    formik.setFieldValue("title", selectedBrand?.title);
    formik.setFieldValue("type", selectedBrand?.type);
    formik.setFieldValue("categoryId", selectedBrand?.category?.id);
  }, [selectedBrand?.id]);

  useEffect(() => {
    handleGetAllCategories();
  }, []);

  return (
    <>
      <div>
        <CustomButton
          Icon={Icons.ArrowLeft}
          IconDirection="left"
          iconStyle="mr-2"
          title="Back"
          containerStyle="outlined-btn-primary mb-3 px-2"
          onClick={handleCancel}
        />
      </div>
      <form
        onSubmit={formik.handleSubmit}
        className={classNames(styles.body, "grid grid-cols-12 gap-2")}
      >
        <div className="col-span-12">
          <CustomSelect
            // @ts-ignore
            options={categories}
            value={formik.values.type}
            label="Product Type"
            onChange={(selectedOption) => {
              formik.setFieldValue("type", selectedOption.value);
            }}
            error={
              formik.touched.type && formik.errors.type
                ? formik.errors.type
                : ""
            }
          />
        </div>

        <div className="col-span-12">
          <CustomInput
            required
            label="Name"
            placeholder="Enter Product Name"
            name="title"
            value={formik.values.title}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.title && formik.errors.title
                ? formik.errors.title
                : ""
            }
          />
        </div>
        <div className="flex justify-end mt-3 w-full col-span-12">
          <CustomButton type="submit" loading={loading} title="Update" />
        </div>
      </form>
    </>
  );
};

export default EditBrands;
